import { useCallback, useEffect, useState } from "react";
import { ListChecks } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState } from "@/components/common/StateBlocks";
import { TaskItem } from "@/components/run/TaskItem";
import { NewTaskDialog } from "@/components/run/NewTaskDialog";
import { runService } from "@/services/runService";
import { useCrmActions } from "@/context/CrmActionsContext";
import { TASK_VIEWS, TASK_CATEGORIES } from "@/config/runOptions";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ALL = "__all__";

export default function Tasks() {
  const { openNewTask } = useCrmActions();
  const [view, setView] = useState("today");
  const [category, setCategory] = useState(ALL);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [editTask, setEditTask] = useState(null);

  const loadMeta = useCallback(() => runService.taskSummary().then(setCounts).catch(() => {}), []);
  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { tasks } = await runService.listTasks({ view, category: category === ALL ? undefined : category });
      setItems(tasks);
    } catch { toast.error("Could not load tasks"); }
    finally { setLoading(false); }
  }, [view, category]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    const h = () => { loadMeta(); loadList(); };
    window.addEventListener("crm:changed", h);
    return () => window.removeEventListener("crm:changed", h);
  }, [loadMeta, loadList]);

  const refresh = () => { loadMeta(); loadList(); };

  return (
    <AppShell>
      <div className="space-y-6" data-testid="tasks-page">
        <PageHeader eyebrow="AgentHQ · Run" title="Tasks"
          description="Organize the work across your business — follow-ups, client care, transactions and admin."
          actions={<Button onClick={() => openNewTask()} data-testid="task-new"><ListChecks className="h-4 w-4" /> New Task</Button>} />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-1 border-b border-border" data-testid="task-tabs">
            {TASK_VIEWS.map((v) => (
              <button key={v.key} onClick={() => setView(v.key)} data-testid={`task-tab-${v.key}`}
                className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${view === v.key ? "border-brand-gold text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                {v.label}
                {counts[v.key] != null && counts[v.key] > 0 && (
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${v.key === "overdue" ? "bg-red-100 text-red-700" : "bg-secondary text-muted-foreground"}`}>{counts[v.key]}</span>
                )}
              </button>
            ))}
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[170px]" data-testid="task-filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All categories</SelectItem>{TASK_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {loading ? <div className="py-10"><LoadingState label="Loading tasks" /></div>
          : items.length === 0 ? (
            <EmptyState title="Nothing needs your attention here yet."
              description="Create a task when there's something you don't want to forget."
              action={<Button className="mt-5" onClick={() => openNewTask()} data-testid="task-empty-new"><ListChecks className="h-4 w-4" /> New Task</Button>} />
          ) : (
            <div className="space-y-2" data-testid="task-list">
              {items.map((t) => <TaskItem key={t.id} task={t} onChanged={refresh} onEdit={setEditTask} />)}
            </div>
          )}
      </div>

      <NewTaskDialog open={!!editTask} onOpenChange={(v) => !v && setEditTask(null)} task={editTask} onCreated={() => { setEditTask(null); refresh(); }} />
    </AppShell>
  );
}
